import { leesActies } from '../mail-acties.js';
import { leesVraagplan, vraagStatus } from '../mail-vraagplan.js';
import { mailAfhandelStatus } from './afhandel-status.js';
import {
  heeftBewezenWhatsappOpmerking,
  leesWhatsappAfhandeling,
  whatsappAfhandelingPresentatie,
  whatsappOpmerkingOpgeslagenPresentatie,
} from './whatsapp-afhandeling.js';
import { isEffectiefKlantbericht, type InboxBronBericht } from './effectieve-klantbron.js';
import { redenVoorSluitenZonderAntwoord, conceptBlokkade, isBevestigingZonderVraag, shouldSkipDraft } from './draft-gates.js';

export type InboxWerkcode =
  | 'mislukt'
  | 'nu'
  | 'handeling_ontbreekt'
  | 'concept_ontbreekt'
  | 'wacht_op_akkoord'
  | 'antwoord_beoordelen'
  | 'sluiten_beoordelen'
  | 'systeemmail'
  | 'geen_antwoord_nodig';

export const INBOX_WERKPRIORITEIT: Record<InboxWerkcode, number> = {
  mislukt: 0,
  nu: 1,
  handeling_ontbreekt: 2,
  concept_ontbreekt: 3,
  wacht_op_akkoord: 4,
  antwoord_beoordelen: 5,
  sluiten_beoordelen: 6,
  systeemmail: 6,
  geen_antwoord_nodig: 6,
};

export interface MailWerkstap {
  conceptBlokkade?: string | null;
  bronMessageId: string | null;
  label: string;
  toon: 'rood' | 'amber' | 'blauw' | 'groen';
  uitleg: string;
  code: InboxWerkcode;
  prioriteit: number;
  blokkeertAfhandelen: boolean;
}
export interface InboxUrgentie { nu: boolean; redenen: string[]; termijn: string | null }
export interface InboxLezerMeta {
  status: 'queued' | 'running' | 'retry' | 'shadowed' | 'done' | 'dead' | 'no_reply' | 'ontbreekt' | 'bron_ontbreekt' | 'onleesbaar' | 'legacy';
  poging: number | null;
  foutcode: string | null;
  schaduwGeslaagd: boolean | null;
}
export interface InboxDuplicateMeta {
  groep: string;
  aantal: number;
  conversationIds: string[];
  crossBrand: boolean;
  canoniekConversationId: string | null;
  reden: 'zelfde_referentie' | 'zelfde_inhoud';
}
export interface InboxAfgeleidMeta {
  lezer: InboxLezerMeta;
  fout: { actief: boolean; code: string | null; label: string | null };
  ongedekt: { actief: boolean; aantal: number; policyBlokkades: number };
  urgentie: InboxUrgentie;
  duplicate: InboxDuplicateMeta | null;
  assignee: { status: 'niet_toegewezen' | 'toegewezen' | 'in_behandeling'; email: string | null };
  blokkeertAfhandelen: boolean;
}

type Row = Record<string, unknown>;
const record = (value: unknown): value is Row => !!value && typeof value === 'object' && !Array.isArray(value);
const lijst = (value: unknown): unknown[] => Array.isArray(value) ? value : [];

function datumBinnen48Uur(tekst: string, now: Date): boolean {
  const matches = [
    ...tekst.matchAll(/\b(20\d{2})-(\d{1,2})-(\d{1,2})\b/g),
    ...tekst.matchAll(/\b(\d{1,2})[-/.](\d{1,2})(?:[-/.](20\d{2}))?\b/g),
  ];
  return matches.some((match) => {
    const iso = match[0].startsWith('20');
    const year = Number(iso ? match[1] : match[3] || now.getFullYear());
    const month = Number(match[2]);
    const day = Number(iso ? match[3] : match[1]);
    const datum = new Date(year, month - 1, day, 23, 59, 59);
    const verschil = datum.getTime() - now.getTime();
    return verschil >= -12 * 3_600_000 && verschil <= 48 * 3_600_000;
  });
}
function werkdagenSinds(iso: unknown, now: Date): number {
  if (typeof iso !== 'string') return 0;
  const start = new Date(iso);
  if (Number.isNaN(start.getTime()) || start >= now) return 0;
  const cursor = new Date(start); cursor.setHours(0, 0, 0, 0);
  const einde = new Date(now); einde.setHours(0, 0, 0, 0);
  let dagen = 0;
  while (cursor < einde && dagen < 31) {
    cursor.setDate(cursor.getDate() + 1);
    if (cursor.getDay() !== 0 && cursor.getDay() !== 6) dagen += 1;
  }
  return dagen;
}

export function bepaalInboxUrgentie(conversation: Row, incoming: Row | null, now = new Date()): InboxUrgentie {
  const tekst = `${String(conversation.subject || '')} ${String(conversation.snippet || '')}`.toLocaleLowerCase('nl-NL');
  const redenen: string[] = [];
  let termijn: string | null = null;
  if (incoming?.ai_klacht_categorie === 'formele_klacht' || incoming?.ai_intent === 'klacht'
    || /\b(klacht|schade|beschadigd|escalatie|aansprakelijk)\b/i.test(tekst)) redenen.push('Klacht of escalatie');
  if (/\b(niet bezorgd|niet geleverd|verkeerd bezorgd|bezorgprobleem|leveringsprobleem|pakket kwijt|track(?:-| )?and(?:-| )?trace)\b/i.test(tekst)) {
    redenen.push('Bezorg- of leveringsprobleem');
  }
  if (/\b(spoed|urgent|dringend|vandaag nog|zo snel mogelijk|uiterlijk (vandaag|morgen)|deadline)\b/i.test(tekst)) {
    redenen.push('Expliciet tijdkritisch');
    termijn = /vandaag/i.test(tekst) ? 'Vandaag' : 'Binnen 48 uur';
  }
  const bronDag = typeof incoming?.message_date === 'string' ? incoming.message_date.slice(0, 10) : null;
  if ((!bronDag || bronDag === now.toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' }))
    && /\b(afspraak|ophalen|ophaal|bezorgen|leveren|monteur|route)\b/i.test(tekst)
    && (/\b(vandaag|morgen|overmorgen)\b/i.test(tekst) || datumBinnen48Uur(tekst, now))) {
    redenen.push('Afspraak binnen 48 uur');
    termijn = /vandaag/i.test(tekst) ? 'Vandaag' : 'Binnen 48 uur';
  }
  const partnerSla = leesActies(incoming?.ai_acties).some((actie) => {
    if (actie.verb !== 'bereid_partnerdoorverwijzing' || !record(actie.uitkomst)) return false;
    const partner = record(actie.uitkomst.partnerconcept) ? actie.uitkomst.partnerconcept : null;
    const vervolg = record(partner?.vervolg) ? partner.vervolg : null;
    return werkdagenSinds(vervolg?.verstuurdAt ?? actie.uitkomst.at, now) > 2;
  });
  if (partnerSla) { redenen.push('Partnertermijn verstreken'); termijn = 'Partner-SLA verstreken'; }
  return { nu: redenen.length > 0, redenen: [...new Set(redenen)], termijn };
}

const lezerMeta = (
  status: InboxLezerMeta['status'],
  poging: number | null,
  foutcode: string | null,
  schaduwGeslaagd: boolean | null = null,
): InboxLezerMeta => ({ status, poging, foutcode, schaduwGeslaagd });

function heeftV2Lezerbewijs(incoming: Row): boolean {
  return incoming.ai_lezer_version != null
    || incoming.ai_context_fingerprint != null
    || incoming.ai_generation_attempt != null;
}

function heeftHistorischBruikbaarResultaat(incoming: Row): boolean {
  if (incoming.ai_draft_status !== 'generated') return false;
  if (typeof incoming.ai_draft_text === 'string' && incoming.ai_draft_text.trim()) return true;
  return leesActies(incoming.ai_acties).some((actie) => actie.verb === 'sluit_mail_zonder_antwoord');
}

function heeftHistorischBeslisteActie(incoming: Row): boolean {
  return leesActies(incoming.ai_acties).some((actie) =>
    actie.uitkomst?.besluit === 'akkoord' || actie.uitkomst?.besluit === 'afgewezen');
}

function isLaterUitgaandBericht(incoming: Row, recent: Row | null): boolean {
  return typeof recent?.id === 'string' && recent.id !== incoming.id && recent.is_incoming === false;
}

function bepaalLezerMeta(incoming: Row | null, recent: Row | null, leesfout: boolean): InboxLezerMeta {
  if (leesfout) return lezerMeta('onleesbaar', null, 'job_status_onleesbaar');
  const job = record(incoming?._lezer_job) ? incoming._lezer_job : null;
  const status = String(job?.status || '');
  if (status === 'shadowed') {
    const schaduwGeslaagd = /^[a-f0-9]{64}$/i.test(String(job?.shadow_result_digest || ''))
      && job?.last_error_code == null;
    return lezerMeta(
      'shadowed',
      Number.isInteger(job?.attempt) ? Number(job?.attempt) : null,
      schaduwGeslaagd
        ? null
        : typeof job?.last_error_code === 'string' && job.last_error_code.trim()
          ? job.last_error_code
          : 'schaduwresultaat_ongeldig',
      schaduwGeslaagd,
    );
  }
  if (['queued', 'running', 'retry', 'done', 'dead', 'no_reply'].includes(status)) {
    return lezerMeta(
      status as InboxLezerMeta['status'],
      Number.isInteger(job?.attempt) ? Number(job?.attempt) : null,
      typeof job?.last_error_code === 'string' ? job.last_error_code : null,
    );
  }
  if (job) return lezerMeta('onleesbaar', null, 'lezerjob_status_ongeldig');
  if (!incoming) return lezerMeta('onleesbaar', null, 'bronbericht_onleesbaar');
  const verklaardeHistorie = !heeftV2Lezerbewijs(incoming)
    && (heeftHistorischBruikbaarResultaat(incoming)
      || heeftHistorischBeslisteActie(incoming)
      || isLaterUitgaandBericht(incoming, recent)
      || (!incoming.inbox_effective_source_type && incoming.ai_draft_status === 'generated'));
  if (verklaardeHistorie) return lezerMeta('legacy', null, null);
  if (incoming.inbox_effective_source_type) return lezerMeta('ontbreekt', null, 'lezerjob_ontbreekt');
  return lezerMeta('bron_ontbreekt', null, 'effectieve_bron_ontbreekt');
}
function bepaalOngedekt(incoming: Row | null): InboxAfgeleidMeta['ongedekt'] {
  const aantal = lijst(incoming?.ai_ongedekt).filter((waarde) => typeof waarde === 'string' && waarde.trim()).length;
  const policyBlokkades = lijst(incoming?.ai_policy_blokkades).length;
  return { actief: aantal + policyBlokkades > 0, aantal, policyBlokkades };
}

interface WerkContext { urgentie: InboxUrgentie; lezer: InboxLezerMeta; ongedekt: InboxAfgeleidMeta['ongedekt'] }
function maakStap(bronMessageId: string | null, label: string, toon: MailWerkstap['toon'], uitleg: string,
  code: InboxWerkcode, context: WerkContext): MailWerkstap {
  const definitieveCode = context.urgentie.nu && !['mislukt', 'sluiten_beoordelen', 'systeemmail', 'geen_antwoord_nodig'].includes(code) ? 'nu' : code;
  return {
    bronMessageId, label, toon, uitleg, code: definitieveCode,
    prioriteit: INBOX_WERKPRIORITEIT[definitieveCode],
    blokkeertAfhandelen: !['geen_antwoord_nodig', 'systeemmail'].includes(definitieveCode),
  };
}

/** Overview only: never authorizes a send, live-process claim or completion metric. */
export function mailWerkstap(conversation: Row, incoming: Row | null, recent: Row | null, leesfout = false): MailWerkstap | null {
  const kanaal = String(conversation.channel || 'email');
  if (!['email', 'whatsapp'].includes(kanaal) || (kanaal === 'email' && conversation.subtype === 'callback')) return null;
  const bronMessageId = typeof incoming?.id === 'string' ? incoming.id : null;
  const context: WerkContext = {
    urgentie: bepaalInboxUrgentie(conversation, incoming),
    lezer: bepaalLezerMeta(incoming, recent, leesfout),
    ongedekt: bepaalOngedekt(incoming),
  };
  const step = (label: string, toon: MailWerkstap['toon'], uitleg: string, code: InboxWerkcode) =>
    maakStap(bronMessageId, label, toon, uitleg, code, context);
  const invalid = () => step('Controle nodig', 'rood', 'De opgeslagen afhandelstatus is onvolledig. Open en controleer het gesprek.', 'mislukt');
  if (!incoming || !recent || !bronMessageId
    || !isEffectiefKlantbericht(incoming as unknown as InboxBronBericht)
    || typeof recent.id !== 'string') return invalid();

  const whatsappAfhandeling = leesWhatsappAfhandeling(incoming.ai_whatsapp_afhandeling);
  if (kanaal === 'whatsapp' && incoming.ai_whatsapp_afhandeling != null && !whatsappAfhandeling) return invalid();
  if (kanaal === 'whatsapp' && whatsappAfhandeling) {
    const presentatie = whatsappAfhandelingPresentatie(whatsappAfhandeling);
    const code: InboxWerkcode = whatsappAfhandeling.status === 'verzenden_mislukt' ? 'mislukt'
      : ['geen_antwoord_nodig', 'antwoord_verstuurd'].includes(whatsappAfhandeling.status) ? 'geen_antwoord_nodig' : 'wacht_op_akkoord';
    return step(presentatie.label, presentatie.toon, presentatie.uitleg, code);
  }
  if (!['nieuw', 'in_behandeling'].includes(String(conversation.status))) return null;

  const blokkade = typeof conversation._concept_blokkade === 'string' ? conversation._concept_blokkade
    : conceptBlokkade({ tekst: String(incoming.ai_draft_text || ''),
        gegenereerdOp: String(incoming.ai_draft_generated_at || incoming.message_date || '') });
  if (blokkade) {
    const sluitbaar = !leesActies(incoming.ai_acties).length && !incoming.ai_vraagplan
      && !context.ongedekt.actief && isBevestigingZonderVraag(String(incoming.body_text || ''));
    return { ...step(sluitbaar ? 'Sluiten zonder antwoord' : 'Concept verouderd', sluitbaar ? 'amber' : 'rood',
      blokkade, sluitbaar ? 'sluiten_beoordelen' : 'mislukt'), conceptBlokkade: blokkade };
  }

  const raw = incoming.ai_acties;
  const acties = leesActies(raw);
  if (raw != null && (!Array.isArray(raw) || acties.length !== raw.length || acties.some(a =>
    !record(a.params) || (a.uitkomst != null && (!record(a.uitkomst) || !['akkoord', 'afgewezen'].includes(a.uitkomst.besluit)))
    || (a.uitvoering != null && (!record(a.uitvoering) || typeof a.uitvoering.gestart_op !== 'string'))))) return invalid();
  if (kanaal === 'whatsapp' && heeftBewezenWhatsappOpmerking(raw)) {
    const presentatie = whatsappOpmerkingOpgeslagenPresentatie();
    return step(presentatie.label, presentatie.toon, presentatie.uitleg, 'mislukt');
  }
  const plan = leesVraagplan(incoming.ai_vraagplan);
  if (incoming.ai_vraagplan != null && (!plan || plan.bron_message_id !== bronMessageId)) return invalid();
  const status = mailAfhandelStatus({ acties, bezig: false, genereren: false,
    fout: incoming.ai_draft_status === 'error' ? 'Het concept is niet gemaakt. Open het gesprek en controleer de fout.' : null,
    context: typeof incoming.ai_actie_context === 'string' && incoming.ai_actie_context.trim() ? incoming.ai_actie_context : null,
    vraagplan: plan });

  if (status.label === 'Uitvoering bezig') return step('Uitvoering controleren', 'amber', 'Een actie is gestart, maar een opgeslagen uitkomst ontbreekt. Controleer dit vóór een nieuwe poging.', 'mislukt');
  if (status.label === 'Uitvoering niet bevestigd' || incoming.ai_draft_status === 'error') {
    return step(status.label === 'Uitvoering niet bevestigd' ? status.label : 'Controle nodig', 'rood', status.uitleg, 'mislukt');
  }
  if (context.lezer.status === 'onleesbaar') return step('Lezerstatus onbekend', 'rood', 'De lezerstatus kon niet veilig worden gelezen. Open en herstel gericht.', 'mislukt');
  if (context.lezer.status === 'shadowed' && context.lezer.schaduwGeslaagd !== true) {
    return step('Schaduwcontrole mislukt', 'rood', 'De schaduwlezer heeft geen geldig resultaat opgeslagen. Controleer de technische details.', 'mislukt');
  }
  if (context.lezer.status === 'dead') return step('Concept definitief mislukt', 'rood', 'De lezer heeft zijn pogingen verbruikt. Herstel de oorzaak en zet gericht opnieuw klaar.', 'concept_ontbreekt');
  if (status.label === 'Afgehandeld zonder antwoord' || context.lezer.status === 'no_reply') {
    return step('Geen antwoord nodig', 'blauw', 'Er is brongebonden bewijs dat geen klantantwoord nodig is.', 'geen_antwoord_nodig');
  }
  if (context.ongedekt.actief) return step('Handeling ontbreekt', 'amber', 'Een gevraagde handeling of policyvoorwaarde is nog niet gedekt. Maak een vraagplan of draag over.', 'handeling_ontbreekt');
  if (['queued', 'running', 'retry'].includes(context.lezer.status)) {
    return step(context.lezer.status === 'retry' ? 'Concept opnieuw in wachtrij' : 'Concept wordt voorbereid', 'blauw', 'De duurzame lezerjob is nog niet terminaal.', 'concept_ontbreekt');
  }
  // Kale no-reply-afzender of autoreply zonder acties: de draft-gate laat de lezer hier
  // bewust niet lopen (Archibus/Partou, Spend Cloud zonder kaart). Dat is geen
  // kapotte keten en geen "geen concept", maar een systeemmail die iemand leest
  // en sluit, of aan een klant koppelt (Willem 17-09: vier Partou-rijen als
  // "Geen concept" in de werkvoorraad).
  if (kanaal === 'email' && !acties.length) {
    const skip = shouldSkipDraft({
      status: typeof conversation.status === 'string' ? conversation.status : null,
      from_email: typeof conversation.from_email === 'string' ? conversation.from_email : null,
      subject: typeof conversation.subject === 'string' ? conversation.subject : null,
      customer_id: typeof conversation.customer_id === 'string' ? conversation.customer_id : null,
    }, {
      is_incoming: incoming.is_incoming === true,
      from_email: typeof incoming.from_email === 'string' ? incoming.from_email : null,
      reply_to_email: typeof incoming.reply_to_email === 'string' ? incoming.reply_to_email : null,
      subject: typeof incoming.subject === 'string' ? incoming.subject : null,
    });
    if (skip && !skip.startsWith('bounce')) {
      return step('Systeemmail', 'blauw', `Automatische afzender (${skip}); de lezer maakt hier geen concept. Lees de melding en handel af, of koppel de klant zodat de lezer meeleest.`, 'systeemmail');
    }
  }
  if (['ontbreekt', 'bron_ontbreekt'].includes(context.lezer.status)) return step('Conceptketen ontbreekt', 'rood', 'Er is geen volledige bron- en lezerketen.', 'concept_ontbreekt');
  if (status.label === 'Wacht op akkoord' && acties.some(a => a.preview?.blokkade && !a.uitkomst)) return step('Controle nodig', 'amber', 'Een actie heeft een blokkade. Open het gesprek.', 'handeling_ontbreekt');
  if (status.label === 'Wacht op akkoord') return step(status.label, status.toon, status.uitleg, 'wacht_op_akkoord');
  if (status.label !== 'Antwoord beoordelen') return step(status.label, status.toon, status.uitleg,
    status.label === 'Antwoord bijwerken' ? 'concept_ontbreekt' : 'antwoord_beoordelen');
  if (plan && (plan.beperkingen.length || !plan.model_volledig || plan.vragen.some(v =>
    ['informatie_nodig', 'overgedragen', 'afgewezen'].includes(vraagStatus(v, acties))))) {
    return step('Informatie of handwerk nodig', 'amber', 'Niet alle vragen zijn afgehandeld. Maak een vraagplan of draag over.', 'handeling_ontbreekt');
  }
  if (recent.id !== incoming.id) return step('Opvolging controleren', 'amber', 'Na dit inkomende bericht staat een later bericht. Controleer het vervolg.', 'concept_ontbreekt');
  const handmatigSluiten = kanaal === 'email' ? redenVoorSluitenZonderAntwoord({
    status: typeof conversation.status === 'string' ? conversation.status : null,
    from_email: typeof conversation.from_email === 'string' ? conversation.from_email : null,
    subject: typeof conversation.subject === 'string' ? conversation.subject : null,
    snippet: typeof conversation.snippet === 'string' ? conversation.snippet : null,
    customer_id: typeof conversation.customer_id === 'string' ? conversation.customer_id : null,
    message_count: typeof conversation.message_count === 'number' ? conversation.message_count : null,
  }, {
    is_incoming: incoming.is_incoming === true,
    from_email: typeof incoming.from_email === 'string' ? incoming.from_email : null,
    reply_to_email: typeof incoming.reply_to_email === 'string' ? incoming.reply_to_email : null,
    subject: typeof incoming.subject === 'string' ? incoming.subject : null,
    body_text: typeof incoming.body_text === 'string' ? incoming.body_text : null,
    body_html: typeof incoming.body_html === 'string' ? incoming.body_html : null,
    has_attachments: incoming.has_attachments === true,
  }) : null;
  if (handmatigSluiten) {
    return step('Sluiten zonder antwoord', 'amber', 'Dit lijkt duidelijk automatisch of commercieel verkeer. Controleer en sluit handmatig zonder antwoord.', 'sluiten_beoordelen');
  }
  if (typeof incoming.ai_draft_text !== 'string' || !incoming.ai_draft_text.trim() || incoming.ai_draft_status !== 'generated') {
    return step('Antwoord voorbereiden', 'blauw', 'Er is nog geen bruikbaar opgeslagen concept. Open het gesprek.', 'concept_ontbreekt');
  }
  return step('Antwoord beoordelen', 'blauw', 'Controleer de tekst. Verzenden blijft een aparte klik; de server controleert dan opnieuw de actuele gegevens.', 'antwoord_beoordelen');
}

function assigneeMeta(conversation: Row): InboxAfgeleidMeta['assignee'] {
  const email = typeof conversation.assigned_to === 'string' && conversation.assigned_to.trim() ? conversation.assigned_to : null;
  return { status: String(conversation.status) === 'in_behandeling' ? 'in_behandeling' : email ? 'toegewezen' : 'niet_toegewezen', email };
}

/** Strip embedded private proposal/draft content before returning list rows. */
export function annoteerInboxGesprek(row: Row): Row {
  const { werkmail, recent, _lezer_fout, ...conversation } = row;
  const one = (rows: unknown): Row | null => Array.isArray(rows) && rows.length === 1 && record(rows[0]) ? rows[0] : null;
  const incoming = one(werkmail), latest = one(recent);
  let urgentie = bepaalInboxUrgentie(conversation, incoming);
  const lezer = bepaalLezerMeta(incoming, latest, _lezer_fout === true);
  const ongedekt = bepaalOngedekt(incoming);
  const werkstap = mailWerkstap(conversation, incoming, latest, _lezer_fout === true);
  if (werkstap?.conceptBlokkade) urgentie = { nu: false, redenen: [], termijn: null };
  const foutActief = werkstap?.code === 'mislukt' || (werkstap?.code === 'concept_ontbreekt' && werkstap.toon === 'rood');
  const inboxMeta: InboxAfgeleidMeta = {
    lezer,
    fout: { actief: foutActief, code: foutActief ? (lezer.foutcode || werkstap?.code || 'afhandeling_mislukt') : null, label: foutActief ? werkstap?.label || 'Herstel nodig' : null },
    ongedekt,
    urgentie,
    duplicate: null,
    assignee: assigneeMeta(conversation),
    blokkeertAfhandelen: werkstap?.blokkeertAfhandelen === true,
  };
  return { ...conversation, mail_werkstap: werkstap, inbox_meta: inboxMeta,
    // Bijlage-icoon in de lijst: het bronbericht (laatste klantmail) heeft bijlagen.
    has_attachments: incoming?.has_attachments === true,
    prebaked_draft: werkstap ? werkstap.label === 'Antwoord beoordelen'
      : !!incoming?.ai_draft_text && incoming.ai_draft_status === 'generated' };
}

function normaliseer(waarde: unknown): string {
  return String(waarde || '').toLocaleLowerCase('nl-NL').normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\b(re|fw|fwd|aw)\s*:/g, '').replace(/[^a-z0-9@]+/g, ' ').trim();
}
function referenties(row: Row): string[] {
  return [...`${row.subject || ''} ${row.snippet || ''}`.toUpperCase().matchAll(/\b(?:L|WB|WI|INV)-?\d{4,}\b/g)]
    .map(match => match[0].replace(/^(L|WB|WI|INV)(\d)/, '$1-$2'));
}
function identiteit(row: Row): string | null {
  if (typeof row.customer_id === 'string' && row.customer_id) return `customer:${row.customer_id}`;
  if (typeof row.from_email === 'string' && row.from_email.includes('@')) return `email:${row.from_email.toLowerCase().trim()}`;
  const telefoon = String(row.phone || '').replace(/\D/g, '');
  return telefoon.length >= 8 ? `phone:${telefoon.slice(-9)}` : null;
}
function hashKort(waarde: string): string {
  let hash = 2166136261;
  for (let i = 0; i < waarde.length; i += 1) { hash ^= waarde.charCodeAt(i); hash = Math.imul(hash, 16777619); }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Waarschuwt alleen; deze afleiding voegt nooit gesprekken samen. */
export function annoteerInboxDuplicaten<T>(rows: T[], tijdvensterUren = 72): T[] {
  const result = rows.map((item) => {
    const row = item as unknown as Row;
    return { ...row, inbox_meta: { ...(record(row.inbox_meta) ? row.inbox_meta : {}), duplicate: null } } as unknown as T;
  });
  const parent = result.map((_, index) => index);
  const find = (index: number): number => parent[index] === index ? index : (parent[index] = find(parent[index]!));
  const union = (a: number, b: number) => { const ra = find(a); const rb = find(b); if (ra !== rb) parent[rb] = ra; };
  const refPairs = new Set<string>();
  for (let a = 0; a < result.length; a += 1) for (let b = a + 1; b < result.length; b += 1) {
    const rowA = result[a]! as unknown as Row; const rowB = result[b]! as unknown as Row;
    const identiteitA = identiteit(rowA);
    if (!identiteitA || identiteitA !== identiteit(rowB)) continue;
    const tijdA = new Date(String(rowA.last_message_at || rowA.created_at || '')).getTime();
    const tijdB = new Date(String(rowB.last_message_at || rowB.created_at || '')).getTime();
    if (!Number.isFinite(tijdA) || !Number.isFinite(tijdB) || Math.abs(tijdA - tijdB) > tijdvensterUren * 3_600_000) continue;
    const refsA = referenties(rowA); const refsB = new Set(referenties(rowB));
    const zelfdeRef = refsA.some(ref => refsB.has(ref));
    const inhoudA = normaliseer(`${rowA.subject || ''} ${rowA.snippet || ''}`).slice(0, 220);
    const inhoudB = normaliseer(`${rowB.subject || ''} ${rowB.snippet || ''}`).slice(0, 220);
    if (!zelfdeRef && !(inhoudA.length >= 16 && inhoudA === inhoudB)) continue;
    union(a, b);
    if (zelfdeRef) refPairs.add(`${a}:${b}`);
  }
  const groepen = new Map<number, number[]>();
  result.forEach((_, index) => { const root = find(index); groepen.set(root, [...(groepen.get(root) || []), index]); });
  for (const indexes of groepen.values()) {
    if (indexes.length < 2) continue;
    const groepRows = indexes.map(index => result[index]! as unknown as Row);
    const crossBrand = new Set(groepRows.map(row => String(row.brand || ''))).size > 1;
    const oudste = [...groepRows].sort((a, b) => new Date(String(a.last_message_at || a.created_at || '')).getTime()
      - new Date(String(b.last_message_at || b.created_at || '')).getTime() || String(a.id).localeCompare(String(b.id)))[0];
    const ids = groepRows.map(row => String(row.id)).sort();
    const reden = [...refPairs].some((pair) => {
      const [a, b] = pair.split(':').map(Number); return indexes.includes(a!) && indexes.includes(b!);
    }) ? 'zelfde_referentie' : 'zelfde_inhoud';
    for (const index of indexes) {
      const row = result[index]! as unknown as Row;
      const meta = row.inbox_meta as unknown as InboxAfgeleidMeta;
      result[index] = { ...row, inbox_meta: { ...meta, duplicate: {
        groep: `dup-${hashKort(ids.join(':'))}`, aantal: ids.length, conversationIds: ids, crossBrand,
        canoniekConversationId: crossBrand ? null : String(oudste!.id), reden,
      } } } as unknown as T;
    }
  }
  return result;
}

export function sorteerInboxWerkvoorraad<T>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const rowA = a as unknown as Row; const rowB = b as unknown as Row;
    const aStap = record(rowA.mail_werkstap) ? rowA.mail_werkstap : null;
    const bStap = record(rowB.mail_werkstap) ? rowB.mail_werkstap : null;
    const prioA = typeof aStap?.prioriteit === 'number' ? aStap.prioriteit : 99;
    const prioB = typeof bStap?.prioriteit === 'number' ? bStap.prioriteit : 99;
    if (prioA !== prioB) return prioA - prioB;
    const tijdA = new Date(String(rowA.last_message_at || rowA.created_at || '')).getTime();
    const tijdB = new Date(String(rowB.last_message_at || rowB.created_at || '')).getTime();
    return (Number.isFinite(tijdA) ? tijdA : Number.MAX_SAFE_INTEGER)
      - (Number.isFinite(tijdB) ? tijdB : Number.MAX_SAFE_INTEGER)
      || String(rowA.id || '').localeCompare(String(rowB.id || ''));
  });
}
